import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actionUpdateSkuAction } from '../../../../redux/action/userSingleReport/actionUserSingleReport';
import { formatReportData } from '../../../../utils/formatReportData';
import { AppDispatch } from '../../../../redux/reducer/store';

import ArrowIcon from '../../../../assets/icons/chevron.svg';
import EditPopup from '../../../custom/editPopup/editPopup';
import Select from '../../../custom/select/select';
import SelectPopup from '../../../custom/selectPopup/selectPopup';
import AiResultCell from '../../../custom/aiResultCell/aiResultCell';
import { IUserOptions } from '../../../../types/interfaces';
import { IReportDictionaryValue, IReportAiResultOptions } from '../../../../types/reportsInterfaces';
import { shouldShowAiResults, findAiServiceResult } from '../../../../utils/aiResults';

interface ISkuActionsOptions extends IReportAiResultOptions {
	action: { id: number, name: string };
	skuId: number;
	skuName: string;
	type: { [key: string]: string };
	value: string;
	hotline?: string;
	changes?: { [key: string]: string }[];
	equals?: { [key: string]: string }[];
	dictionaryValues?: IReportDictionaryValue[];
}

const getSkuActionValueType = (action: ISkuActionsOptions) =>
	Object.values(action.type).join('');

const isSkuActionValueSelectType = (type: string) =>
	type === 'Фото' || type === 'Да/Нет' || type === 'Справочник';

const getSkuActionValueSelectOptions = (action: ISkuActionsOptions) => {
	const type = getSkuActionValueType(action);

	if (type === 'Фото') {
		return [
			{ value: 'Сделано', label: 'Сделано' },
			{ value: 'Не сделано', label: 'Не сделано' },
		];
	}
	if (type === 'Да/Нет') {
		return [
			{ value: 'Да', label: 'Да' },
			{ value: 'Нет', label: 'Нет' },
		];
	}
	if (type === 'Справочник') {
		return (action.dictionaryValues ?? []).map(({ name }) => ({
			value: name,
			label: name,
		}));
	}
	return [];
};

const SkuActionsTableSection: FC = () => {
	const dispatch = useDispatch<AppDispatch>();

	const { report, aiReviewSummary } = useSelector(
		(state: any) => state.userSingleReport.userSingleReport
	);
	const { claims, isCustomer, settings: userSettings } = useSelector(
		(state: IUserOptions) => state.user.user.data
	);
	const showAiResults = shouldShowAiResults(
		isCustomer,
		userSettings?.aiReview,
		claims
	);

	const [openActionValueIndex, setOpenActionValueIndex] = useState<number>(-1);
	const [newSkuActionValue, setNewSkuActionValue] = useState<string>('');
	const [isVisibleData, setIsVisibleData] = useState<boolean>(true);

	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		document.addEventListener('mousedown', handleDocumentClick);

		return () => {
			document.removeEventListener('mousedown', handleDocumentClick);
		};
	}, []);

	const handleClickCancelRenameBtn = () => {
		setOpenActionValueIndex(-1);
	};

	const handleChangeReportData = (index: number, section: string) => {
		const sectionToStateMap: any = {
			actionValue: {
				setState: setOpenActionValueIndex,
				state: openActionValueIndex,
			},
		};

		const setOpenIndexFunction = sectionToStateMap[section];

		if (setOpenIndexFunction) {
			if (index === setOpenIndexFunction.state) {
				return;
			} else {
				setOpenIndexFunction.setState(index);
			}
		}
	};

	const handleAcceptRenameSkuAction = (action: ISkuActionsOptions, typeOfValue: string) => {
		dispatch(
			actionUpdateSkuAction({
				reportId: report && report.reportId.id,
				data: [
					{
						action: {
							id: action.action.id,
						},
						sku: {
							id: action.skuId,
							name: action.skuName,
						},
						hotline: action.hotline,
						value:
							typeOfValue === 'value'
								? newSkuActionValue || action.value
								: action.value,
					},
				],
			})
		);

		handleClickCancelRenameBtn();
		setNewSkuActionValue('');
	};

	const handleHotlineSelectChange = (
		selectedOption: { value: string; label: string },
		action: ISkuActionsOptions
	) => {
		dispatch(
			actionUpdateSkuAction({
				reportId: report && report.reportId.id,
				data: [
					{
						action: {
							id: action.action.id,
						},
						sku: {
							id: action.skuId,
							name: action.skuName,
						},
						hotline: selectedOption.value,
						value: action.value,
					},
				],
			})
		);
	};

	const handleAcceptEditOnKeyPress = (
		event: React.KeyboardEvent<HTMLInputElement>,
		action: ISkuActionsOptions,
		type: string
	) => {
		if (event.key === 'Enter') {
			handleAcceptRenameSkuAction(action, type);
		}
	};

	const handleDocumentClick = (event: MouseEvent) => {
		const target = event.target as Element;
		if (
			target.closest('[role="listbox"]') ||
			target.closest('[role="option"]')
		) {
			return;
		}
		if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
			setOpenActionValueIndex(-1);
		}
	};

	return (
		<div className={`sectionData ${isVisibleData ? 'sectionData_active  sectionData__overflow' : ''}`}>
			<span className='section__title'>Акции по товарам</span>
			<button
				className='sectionData_button'
				onClick={() => setIsVisibleData(!isVisibleData)}
			>
				<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
			</button>
			<table className='customerOutlet__data_table'>
				<thead className='tunder__thead'>
					<tr>
						<th>Акция</th>
						<th>Наименование</th>
						<th>Значение</th>
						<th>Горячая линия</th>
						{showAiResults && <th>ИИ</th>}
					</tr>
				</thead>
				<tbody>
					{report &&
						report?.skuActions?.map((action: ISkuActionsOptions, index: number) => {
							const skuActionValueType = getSkuActionValueType(action);

							return (
								<tr key={index}>
									<td>{action.action.name}</td>
									<td>{action.skuName}</td>
									<td
										onClick={() => {
											if (
												claims.includes('admin') ||
												claims.includes('reports:edit')
											) {
												handleChangeReportData(index, 'actionValue');
											}
										}}
										className='edit__data_text'
									>
										<span
											className={
												formatReportData(action?.changes)?.includes('value')
													? 'changed__table_value'
													: formatReportData(action?.equals)?.includes('Value')
														? 'previous__table_value'
														: ''
											}
										>
											{action.value}
										</span>
										{openActionValueIndex === index &&
											(isSkuActionValueSelectType(skuActionValueType) ? (
												<SelectPopup
													handleClickAcceptBtn={() =>
														handleAcceptRenameSkuAction(action, 'value')
													}
													handleClickCancelBtn={handleClickCancelRenameBtn}
													handleChangeValue={(selectedOption) => {
														setNewSkuActionValue(selectedOption.value);
													}}
													value={newSkuActionValue}
													defaultValue={action.value}
													options={getSkuActionValueSelectOptions(action)}
													modalRef={modalRef}
												/>
											) : (
												<EditPopup
													handleClickAcceptRenameBtn={() =>
														handleAcceptRenameSkuAction(action, 'value')
													}
													handleAcceptOnKeyPress={(
														event: React.KeyboardEvent<HTMLInputElement>
													) => handleAcceptEditOnKeyPress(event, action, 'value')}
													handleClickCancelRenameBtn={handleClickCancelRenameBtn}
													handleChangeValue={(
														event: React.ChangeEvent<HTMLInputElement>
													) => {
														setNewSkuActionValue(event?.target.value);
													}}
													defaultValue={action.value}
													className='edit__popup'
													modalRef={modalRef}
												/>
											))}
									</td>
									<td style={{ minWidth: 200 }}>
										<Select
											value={{
												value: action.hotline || '',
												label: action.hotline || ' ',
											}}
											options={[
												{ value: '', label: '\u00A0' },
												...(report?.actionHotlines?.map((h: string) => ({
													value: h,
													label: h,
												})) || []),
											]}
											name="actionHotlines"
											propsChange={(selectedOption: {
												value: string;
												label: string;
											}) => {
												handleHotlineSelectChange(selectedOption, action);
											}}
											style={{ width: '100%' }}
											isHotlineChanged={
												action?.changes &&
												formatReportData(action?.changes)?.includes('hotline')
											}
										/>
									</td>
									{showAiResults && (
										<td className='ai-result-cell'>
											<AiResultCell
												item={findAiServiceResult(
													aiReviewSummary?.services,
													{
														name: action.action.name,
														sku: action.skuName,
														section: 'skuActions',
													},
													action
												)}
											/>
										</td>
									)}
								</tr>
							);
						})}
				</tbody>
			</table>
		</div>
	);
};

export default SkuActionsTableSection;
